import { UserCircle, Calendar, CreditCard, Sparkles, Clock } from "lucide-react";

// Mission Watch — Account details card. Shows the customer their signup date,
// current plan, trial/renewal date, last login, and lifetime account age so they
// always know exactly where they stand without digging into billing.

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch { return "—"; }
}

function fmtDateFromUnix(sec) {
  if (!sec) return "—";
  try {
    return new Date(sec * 1000).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch { return "—"; }
}

function daysSince(iso) {
  if (!iso) return null;
  try {
    const ms = Date.now() - new Date(iso).getTime();
    return Math.max(0, Math.floor(ms / 86400000));
  } catch { return null; }
}


export default function AccountDetails({ user, subscription }) {
  const created = user?.created_at || subscription?.created_at;
  const memberDays = daysSince(created);
  const plan = subscription?.plan;
  const planLabel = plan === "complete" ? "Protection" : plan === "essential" ? "Monthly Checkup" : "None";
  const status = subscription?.status || "inactive";
  const isTrial = subscription?.is_trialing;
  const willCancel = subscription?.cancel_at_period_end;

  // Next billing date: trial_end during trial, else current_period_end.
  const nextBillingUnix = isTrial ? subscription?.trial_end : subscription?.current_period_end;
  const nextBillingLabel = isTrial ? "Trial ends & billing begins"
    : willCancel ? "Cancels on" : "Next renewal";

  const rows = [
    {
      icon: Calendar,
      label: "Member since",
      value: created ? fmtDate(created) : "—",
      hint: memberDays !== null
        ? `${memberDays} day${memberDays === 1 ? "" : "s"} with Mission Watch`
        : null,
      testid: "acct-member-since",
    },
    {
      icon: CreditCard,
      label: "Current plan",
      value: planLabel + (isTrial ? " · Trial" : status === "active" ? " · Active" : status === "canceled" ? " · Canceled" : ""),
      hint: subscription?.amount ? `$${Number(subscription.amount).toFixed(2)} / month` : null,
      testid: "acct-plan",
    },
    {
      icon: Sparkles,
      label: nextBillingLabel,
      value: fmtDateFromUnix(nextBillingUnix),
      hint: willCancel ? "You won't be charged again" : (isTrial ? "First charge on this date" : null),
      testid: "acct-renewal",
    },
    {
      icon: Clock,
      label: "Last sign-in",
      value: user?.last_login_at ? fmtDate(user.last_login_at)
        : subscription?.last_login_at ? fmtDate(subscription.last_login_at) : "—",
      hint: null,
      testid: "acct-last-login",
    },
  ];

  return (
    <section className="mt-6 rounded-2xl bg-white border border-black/5 shadow-soft p-6" data-testid="account-details-card">
      <div className="flex items-center gap-2 mb-1">
        <UserCircle className="w-4 h-4 text-brand" />
        <h2 className="text-lg font-medium text-charcoal-900">Account details</h2>
      </div>
      <p className="text-sm text-charcoal-500 mb-5">
        A quick snapshot of your account, billing dates, and history with Mission Watch.
      </p>
      <div className="grid sm:grid-cols-2 gap-3">
        {rows.map(({ icon: Icon, label, value, hint, testid }) => (
          <div
            key={label}
            className="flex items-start gap-3 py-3.5 px-4 rounded-xl border border-black/5 bg-charcoal-50/50"
            data-testid={testid}
          >
            <div className="w-9 h-9 rounded-lg bg-white border border-black/5 grid place-items-center shrink-0">
              <Icon className="w-4 h-4 text-charcoal-500" strokeWidth={2} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-charcoal-400">{label}</p>
              <p className="text-sm font-medium text-charcoal-900 mt-0.5">{value}</p>
              {hint && <p className="text-[11px] font-mono text-charcoal-400 mt-0.5">{hint}</p>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
